<script setup lang="ts">
import {reactive, ref} from "vue";
import {TestReel} from "@/types";
import {getDateInfo, getTimeStr} from "@/utils/common.ts";
import {TestService} from "@/api/api.ts";
import PageLabel from "components/common/PageLabel.vue";
import AllTestCateSelector from "components/test/AllTestCateSelector.vue";
import router from "@/router";
import BatchAddQuestionDialog from "components/dialog/test/BatchAddQuestionDialog.vue";

const batchAddQuestionDialog = ref<InstanceType<typeof BatchAddQuestionDialog>>()
const testCateSelector = ref<InstanceType<typeof AllTestCateSelector>>()

const loading = ref(false)
const range = ref<Date[]>([])
const status = ref<number>()
const content = ref('')
const pagination = reactive({
  current: 1,
  pageSize: 10,
  total: 0
})
const reelList = ref<TestReel[]>()

const getReelList = async () => {
  try {
    loading.value = true
    const {start_time, end_time} = getDateInfo(range.value)
    const res = await TestService.getReelList({
      page: pagination.current,
      pageSize: pagination.pageSize,
      f_id: testCateSelector.value?.getFCateId(),
      s_id: testCateSelector.value?.getSCateId(),
      status: status.value === -1 ? undefined : status.value,
      content: content.value,
      start_time: start_time,
      end_time: end_time
    })
    if (res.data.code === 200) {
      reelList.value = res.data.data?.list
      pagination.current = res.data.data?.page || 1
      pagination.total = res.data.data?.count || 0
    }
  } catch (err) {
    console.warn(err)
  } finally {
    loading.value = false
  }
}

const onSearch = () => {
  pagination.current = 1
  getReelList()
}

const onReset = () => {
  content.value = ""
  range.value = []
  status.value = undefined
  testCateSelector.value?.reset()
  onSearch()
}

const onPageChange = (page: number) => {
  pagination.current = page
  getReelList()
}

const onSizeChange = (pageSize: number) => {
  pagination.pageSize = pageSize
  getReelList()
}

const toReelInfo = (id: number) => {
  router.push({
    path: "/creator/test/list",
    query: {
      reel_id: id
    }
  })
}

const onBatchAdd = () => {
  batchAddQuestionDialog.value?.show(2)
}

const onBatchAddSuccess = () => {
  onReset()
}

getReelList()
</script>

<template>
  <div class="mx-3rem mt-2.25rem">
    <page-label>套卷列表</page-label>
    <div class="p-1rem bg-white rounded-md mt-2rem shadow-md gap-1rem">
      <el-button type="primary" @click="onBatchAdd">套卷上传</el-button>
      <div class="mt-1rem">
        <el-form inline >
          <all-test-cate-selector ref="testCateSelector"/>
          <el-form-item label="启用状态">
            <el-select class="my-selector" v-model="status" placeholder="请选择状态">
              <el-option label="全部" :value="-1"></el-option>
              <el-option label="已启用" :value="1"></el-option>
              <el-option label="未启用" :value="0"></el-option>
            </el-select>
          </el-form-item>
        </el-form>
        <el-form inline>
          <el-form-item label="搜索内容">
            <el-input class="my-input" v-model="content" placeholder="请输入关键词" @change="onSearch" />
          </el-form-item>
          <el-form-item label="上传时间">
            <el-date-picker
                v-model="range"
                type="datetimerange"
                range-separator="至"
                start-placeholder="开始时间"
                end-placeholder="结束时间"
                style="width: 20rem"
            />
          </el-form-item>
          <el-form-item>
            <el-button type="primary" @click="onSearch">搜索</el-button>
            <el-button type="danger" @click="onReset">重置</el-button>
          </el-form-item>
        </el-form>
      </div>
    </div>
    <div class="p-1rem bg-white rounded-md mt-1rem shadow-md" v-loading="loading">
      <el-table :data="reelList">
        <el-table-column prop="id" label="ID"></el-table-column>
        <el-table-column prop="name" label="套卷名称"></el-table-column>
        <el-table-column prop="cate_id" label="分类ID"></el-table-column>
        <el-table-column prop="s_cate_name" label="分类名称"></el-table-column>
        <el-table-column prop="pid" label="PID"></el-table-column>
        <el-table-column prop="one_cate_id" label="分类1 ID">
          <template #default="scope">
            {{ scope.row.one_cate_id || '--' }}
          </template>
        </el-table-column>
        <el-table-column prop="one_cate_name" label="分类1 名称">
          <template #default="scope">
            {{ scope.row.one_cate_name || '--' }}
          </template>
        </el-table-column>
        <el-table-column prop="two_cate_id" label="分类2 ID">
          <template #default="scope">
            {{ scope.row.two_cate_id || '--' }}
          </template>
        </el-table-column>
        <el-table-column prop="two_cate_name" label="分类2 名称">
          <template #default="scope">
            {{ scope.row.two_cate_name || '--' }}
          </template>
        </el-table-column>
        <el-table-column prop="three_cate_id" label="分类3 ID">
          <template #default="scope">
            {{ scope.row.three_cate_id || '--' }}
          </template>
        </el-table-column>
        <el-table-column prop="three_cate_name" label="分类3 名称">
          <template #default="scope">
            {{ scope.row.three_cate_name || '--' }}
          </template>
        </el-table-column>
        <el-table-column prop="topic_sum" label="题数"></el-table-column>
        <el-table-column prop="create_time" label="创建时间">
          <template #default="scope">
            {{ getTimeStr(scope.row.create_time * 1000) }}
          </template>
        </el-table-column>
        <el-table-column prop="" label="操作">
          <template #default="scope">
            <el-button type="primary" @click="toReelInfo(scope.row.id)" >查看</el-button>
          </template>
        </el-table-column>
      </el-table>
      <el-pagination
          class="mt-1rem"
          background
          :page-sizes="[10, 20, 50, 100]"
          layout="prev, pager, next, jumper, ->, total, sizes"
          :current-page="pagination.current"
          :page-size="pagination.pageSize"
          :total="pagination.total"
          @current-change="onPageChange"
          @size-change="onSizeChange"/>
      <batch-add-question-dialog ref="batchAddQuestionDialog" @on-batch-add-success="onBatchAddSuccess"/>
    </div>
  </div>
</template>

<style scoped>
</style>
